import { useCallback, useState } from 'react';
import { KINETIC_MODULES, LazyComponent, ModuleImports, renderLazyModules } from './modules';
import { useLazyModuleImports } from './useLazyModuleImports';
import { useInterval } from '@qfa/react-components';

type UseRenderLazyOptions = {
  imports: ModuleImports<LazyComponent>;
  pick?: KINETIC_MODULES[];
  omit?: KINETIC_MODULES[];
  props?: unknown;
};

export const useRenderLazy = ({ imports, pick, omit, props }: UseRenderLazyOptions) => {
  const lazyImports = useLazyModuleImports({ imports, pick, omit });
  return renderLazyModules(lazyImports, props);
};

export const areLazyModulesLoaded = (imported: ReturnType<typeof useRenderLazy>) => {
  return imported.every((comp) => {
    const status = comp.type._payload._status;
    /**
     * https://github.com/facebook/react/blob/main/packages/react/src/ReactLazy.js#L15-L18
     * Uninitialized = -1;
     * Pending = 0;
     * Resolved = 1;
     * Rejected = 2;
     */
    return status !== 0 && status !== -1;
  });
};

export const areLazyModulesEmpty = (imported: ReturnType<typeof useRenderLazy>) => {
  const allReady = areLazyModulesLoaded(imported);
  if (!allReady) {
    return false;
  }

  const allMissed = imported.every((comp) => {
    const payload = comp.type._payload;
    return payload._status === 2 || payload._result?.__missedImport === true;
  });
  return allMissed;
};

export const useDetectEmptyLazyModules = ({
  imports,
}: {
  imports: ReturnType<typeof useRenderLazy>;
}) => {
  const [allEmpty, setAllEmpty] = useState<boolean | null>(null);

  const onInterval = useCallback(() => {
    if (!areLazyModulesLoaded(imports)) {
      return;
    }

    setAllEmpty(areLazyModulesEmpty(imports));
  }, [imports]);

  useInterval(onInterval, allEmpty === null ? 50 : null);

  return allEmpty;
};
